import {
  generateTrouserPattern,
  TROUSER_CONTRACT,
  TROUSER_ACTIVE_FORMULA_VERSION,
  TROUSER_ACTIVE_GEOMETRY_VERSION,
  TROUSER_DEFAULT_MEASUREMENTS,
  TROUSER_DISPLAY_OPTIONS,
  resolveTrouserFormulaVersionFromGeometry,
  resolveTrouserGeometryVersionFromFormula,
} from '@/lib/patterns/trouser';
import {
  generateShirtPattern,
  SHIRT_CONTRACT,
  SHIRT_ACTIVE_FORMULA_VERSION,
  SHIRT_ACTIVE_GEOMETRY_VERSION,
  SHIRT_GEOMETRY_V2,
  SHIRT_DEFAULT_MEASUREMENTS,
  SHIRT_DISPLAY_OPTIONS,
  resolveShirtFormulaVersionFromGeometry,
  resolveShirtGeometryVersionFromFormula,
} from '@/lib/patterns/shirt';
import {
  generateWaistcoatPattern,
  WAISTCOAT_DEFAULT_MEASUREMENTS,
  WAISTCOAT_DISPLAY_OPTIONS,
  WAISTCOAT_CONTRACT,
} from '@/lib/patterns/waistcoat';
import {
  generateJacketPattern,
  JACKET_DEFAULT_MEASUREMENTS,
  JACKET_DISPLAY_OPTIONS,
  JACKET_CONTRACT,
  JACKET_ACTIVE_FORMULA_VERSION,
  JACKET_ACTIVE_GEOMETRY_VERSION,
  normalizeJacketGeometryVersion,
  resolveJacketFormulaVersionFromGeometry,
  resolveJacketGeometryVersionFromFormula,
} from '@/lib/patterns/jacket';
import {
  generateOvercoatPattern,
  OVERCOAT_DEFAULT_MEASUREMENTS,
  OVERCOAT_DISPLAY_OPTIONS,
} from '@/lib/patterns/overcoat';

const GARMENT_CONFIG = {
  trouser: {
    label: 'Trouser',
    status: 'ready',
    formulaVersion: TROUSER_ACTIVE_FORMULA_VERSION,
    geometryVersion: TROUSER_ACTIVE_GEOMETRY_VERSION,
    resolveGeometryVersionFromFormula: resolveTrouserGeometryVersionFromFormula,
    resolveFormulaVersionFromGeometry: resolveTrouserFormulaVersionFromGeometry,
    legacyDefaultGeometryVersion: resolveTrouserGeometryVersionFromFormula('siy_v2'),
    defaults: TROUSER_DEFAULT_MEASUREMENTS,
    displayOptions: TROUSER_DISPLAY_OPTIONS,
    generator: generateTrouserPattern,
    contract: TROUSER_CONTRACT,
  },
  shirt: {
    label: 'Shirt',
    status: 'ready',
    formulaVersion: SHIRT_ACTIVE_FORMULA_VERSION,
    geometryVersion: SHIRT_ACTIVE_GEOMETRY_VERSION,
    resolveGeometryVersionFromFormula: resolveShirtGeometryVersionFromFormula,
    resolveFormulaVersionFromGeometry: resolveShirtFormulaVersionFromGeometry,
    legacyDefaultGeometryVersion: SHIRT_GEOMETRY_V2,
    defaults: SHIRT_DEFAULT_MEASUREMENTS,
    displayOptions: SHIRT_DISPLAY_OPTIONS,
    generator: generateShirtPattern,
    contract: SHIRT_CONTRACT,
  },
  waistcoat: {
    label: 'Waistcoat',
    status: 'planned',
    formulaVersion: 'pending',
    geometryVersion: 'pending',
    legacyDefaultGeometryVersion: 'pending',
    defaults: WAISTCOAT_DEFAULT_MEASUREMENTS,
    displayOptions: WAISTCOAT_DISPLAY_OPTIONS,
    generator: generateWaistcoatPattern,
    contract: WAISTCOAT_CONTRACT,
  },
  jacket: {
    label: 'Jacket',
    status: 'ready',
    formulaVersion: JACKET_ACTIVE_FORMULA_VERSION,
    geometryVersion: JACKET_ACTIVE_GEOMETRY_VERSION,
    resolveGeometryVersionFromFormula: resolveJacketGeometryVersionFromFormula,
    resolveFormulaVersionFromGeometry: resolveJacketFormulaVersionFromGeometry,
    normalizeGeometryVersion: normalizeJacketGeometryVersion,
    legacyDefaultGeometryVersion: JACKET_ACTIVE_GEOMETRY_VERSION,
    defaults: JACKET_DEFAULT_MEASUREMENTS,
    displayOptions: JACKET_DISPLAY_OPTIONS,
    generator: generateJacketPattern,
    contract: JACKET_CONTRACT,
  },
  overcoat: {
    label: 'Overcoat',
    status: 'planned',
    formulaVersion: 'pending',
    geometryVersion: 'pending',
    legacyDefaultGeometryVersion: 'pending',
    defaults: OVERCOAT_DEFAULT_MEASUREMENTS,
    displayOptions: OVERCOAT_DISPLAY_OPTIONS,
    generator: generateOvercoatPattern,
  },
};

export const GARMENT_ROADMAP = ['trouser', 'shirt', 'waistcoat', 'jacket', 'overcoat'];

function normalizeGarmentType(garmentType) {
  const normalized = (garmentType || 'trouser').toLowerCase();
  return GARMENT_CONFIG[normalized] ? normalized : 'trouser';
}

export function getGarmentConfig(garmentType) {
  return GARMENT_CONFIG[normalizeGarmentType(garmentType)];
}

export function getGarmentContract(garmentType) {
  const config = getGarmentConfig(garmentType);
  return config?.contract || null;
}

export function getDefaultMeasurementsByGarment(garmentType) {
  const config = getGarmentConfig(garmentType);
  return { ...config.defaults };
}

export function resolveGeometryVersionByGarment(garmentType, patternLike = {}) {
  const config = getGarmentConfig(garmentType);

  const explicitGeometry = patternLike?.geometry_version || patternLike?.pattern_data?.geometryVersion;
  if (explicitGeometry) {
    return config?.normalizeGeometryVersion ? config.normalizeGeometryVersion(explicitGeometry) : explicitGeometry;
  }

  const explicitFormula = patternLike?.formula_version || patternLike?.pattern_data?.formulaVersion;
  if (explicitFormula && config.resolveGeometryVersionFromFormula) {
    return config.resolveGeometryVersionFromFormula(explicitFormula);
  }

  if (patternLike?.isNew) {
    return config.geometryVersion;
  }

  return config.legacyDefaultGeometryVersion || config.geometryVersion;
}

export function resolveFormulaVersionByGarment(garmentType, patternLike = {}) {
  const config = getGarmentConfig(garmentType);

  if (patternLike?.formula_version) return patternLike.formula_version;
  if (patternLike?.pattern_data?.formulaVersion) return patternLike.pattern_data.formulaVersion;

  const geometryVersion = patternLike?.geometry_version
    || patternLike?.pattern_data?.geometryVersion
    || config.geometryVersion;

  if (config.resolveFormulaVersionFromGeometry) {
    return config.resolveFormulaVersionFromGeometry(geometryVersion);
  }

  return config.formulaVersion;
}

export function getSupportedGeometryVersionsByGarment(garmentType) {
  const config = getGarmentConfig(garmentType);
  const supported = Array.isArray(config?.contract?.supportedVersions)
    ? config.contract.supportedVersions
    : [];
  const fallback = config?.geometryVersion ? [config.geometryVersion] : [];
  const unique = [...new Set((supported.length ? supported : fallback).filter(Boolean))];
  const active = config?.geometryVersion || null;
  const parseVersion = (value) => {
    const match = String(value || '').match(/_v(\d+)$/i);
    return match ? Number(match[1]) : -1;
  };

  const sorted = [...unique].sort((a, b) => {
    if (active && a === active && b !== active) return -1;
    if (active && b === active && a !== active) return 1;

    const av = parseVersion(a);
    const bv = parseVersion(b);
    if (av !== bv) return bv - av;

    return String(b).localeCompare(String(a));
  });

  return sorted;
}

export function generatePatternByGarment(garmentType, measurements, controls = {}) {
  const normalized = normalizeGarmentType(garmentType);
  const config = GARMENT_CONFIG[normalized];
  const geometryVersion = controls.geometryVersion || config.geometryVersion;
  const formulaVersion = controls.formulaVersion
    || (config.resolveFormulaVersionFromGeometry
      ? config.resolveFormulaVersionFromGeometry(geometryVersion)
      : config.formulaVersion);

  const generated = config.generator(measurements, {
    ...controls,
    geometryVersion,
    formulaVersion,
  });

  return {
    ...generated,
    formulaVersion: generated?.formulaVersion || formulaVersion,
    geometryVersion: generated?.geometryVersion || geometryVersion,
  };
}

export function getGarmentRoadmap() {
  return GARMENT_ROADMAP.map((key) => {
    const config = GARMENT_CONFIG[key];
    return {
      key,
      label: config.label,
      status: config.status,
      formulaVersion: config.formulaVersion,
      geometryVersion: config.geometryVersion,
    };
  });
}
